import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download, KeyRound, ChevronDown, Puzzle } from 'lucide-react';

interface Integration {
  id: string;
  name: string;
  host: string;
  platform: string;
  requirements: string;
  blurb: string;
  href: string;
  steps: string[];
}

// Static catalog. The download hrefs point to zips produced by
// scripts/build-integrations.mjs into frontend/public/integrations/ at build time
// and served by Firebase Hosting.
const INTEGRATIONS: Integration[] = [
  {
    id: 'photoshop-panel',
    name: 'Adobe Photoshop',
    host: 'UXP panel',
    platform: 'macOS · Windows',
    requirements: 'Photoshop 26.0+ (2025 or later)',
    blurb:
      'A docked panel that runs AICS Pro workflows on your active document. Image inputs ' +
      'default to the open document; results can open as new documents or land as new layers.',
    href: '/integrations/photoshop-panel.ccx',
    steps: [
      'Download and double-click the .ccx — Creative Cloud Desktop installs the plugin on macOS and Windows alike (no developer tools needed).',
      'Open it in Photoshop via Plugins → AICS Pro Workflows.',
      'In the panel: choose Production, paste a Tool Key, Connect.',
      'Optional: check “Place results as new layers” to have each image result land on its own layer in the active document.',
    ],
  },
  {
    id: 'premiere-panel',
    name: 'Adobe Premiere Pro',
    host: 'UXP panel',
    platform: 'macOS · Windows',
    requirements: 'Premiere Pro 2026 (25.6) or later',
    blurb:
      'A docked panel that runs AICS Pro workflows on your timeline. Image inputs default to the ' +
      'playhead frame of the active sequence (or a selected clip / file); results import into the project.',
    href: '/integrations/premiere-panel.ccx',
    steps: [
      'Download and double-click the .ccx — Creative Cloud Desktop installs the plugin on macOS and Windows alike (no developer tools needed).',
      'Open it in Premiere via Window → Extensions (UXP) → AICS Pro Workflows.',
      'In the panel: choose Production, paste a Tool Key, Connect.',
      'For an image input, pick a source: playhead frame, selected timeline clip, or a file.',
    ],
  },
  {
    id: 'aftereffects-panel',
    name: 'Adobe After Effects',
    host: 'CEP extension',
    platform: 'macOS · Windows',
    requirements: 'After Effects CC 2019 or later',
    blurb:
      'Runs AICS Pro workflows on your comps and footage. Image inputs default to the ' +
      'active comp’s current frame; outputs are imported into the project.',
    href: '/integrations/aftereffects-panel.zip',
    steps: [
      'Unzip into the CEP extensions folder — macOS: ~/Library/Application Support/Adobe/CEP/extensions/ · ' +
        'Windows: %APPDATA%\\Adobe\\CEP\\extensions\\',
      'Enable CEP debug mode so the unsigned panel loads — macOS: defaults write com.adobe.CSXS.11 PlayerDebugMode 1 · ' +
        'Windows: reg add HKCU\\Software\\Adobe\\CSXS.11 /v PlayerDebugMode /t REG_SZ /d 1 (see the README for your AE version).',
      'Relaunch After Effects → Window → Extensions → AICS Pro Workflows.',
      'Turn on Settings → Scripting & Expressions → “Allow Scripts to Write Files and Access Network” ' +
        '(required — the panel reads/writes frames and outputs on disk).',
      'In the panel: choose Production, paste a Tool Key, Connect.',
    ],
  },
  {
    id: 'resolve-plugin',
    name: 'DaVinci Resolve Studio',
    host: 'Workflow Integration plugin',
    platform: 'macOS · Windows',
    requirements: 'DaVinci Resolve Studio 19+ (Studio only)',
    blurb:
      'A docked panel that runs workflows on Media Pool clips, the playhead frame, or the timeline clip, and imports outputs into the current bin.',
    href: '/integrations/resolve-plugin.zip',
    steps: [
      'Unzip the download to any folder.',
      'macOS: double-click “Install AICS Workflow Runner.command” (first time only, if macOS blocks it: right-click → Open). ' +
        'Enter your Mac password when prompted.',
      'Windows: right-click “Install AICS Workflow Runner.bat” → Run as administrator.',
      'The installer copies the plugin and pulls the required WorkflowIntegration.node bridge from your Resolve Studio install automatically.',
      'Fully quit (⌘Q) and relaunch DaVinci Resolve so it picks up the new plugin.',
      'Open Workspace → Workflow Integrations → AICS Workflow Runner; paste a Tool Key, Connect.',
    ],
  },
  {
    id: 'cinema4d-plugin',
    name: 'Maxon Cinema 4D',
    host: 'Python plugin',
    platform: 'macOS · Windows',
    requirements: 'Cinema 4D 2024 or later',
    blurb:
      'A dockable panel that runs AICS Pro workflows from inside Cinema 4D. Feed a viewport ' +
      'render, a depth pass, a selected object’s texture, or a file; results come back as ' +
      'materials (PBR sets supported) and meshes are merged into the scene.',
    href: '/integrations/cinema4d-plugin.zip',
    steps: [
      'Unzip and copy the cinema4d-plugin folder into your Cinema 4D user plugins folder — ' +
        'macOS: ~/Library/Preferences/Maxon/Maxon Cinema 4D 2025_XXXXXXXX/plugins/ · ' +
        'Windows: %APPDATA%\\Maxon\\Maxon Cinema 4D 2025_XXXXXXXX\\plugins\\ (see the included README).',
      'Restart Cinema 4D, then open Extensions → AICS Pro Workflows (dock it anywhere).',
      'Choose Production, paste a Tool Key, Connect (the key is saved so you only enter it once).',
      'Pick a workflow; for image inputs choose a source (Viewport render / Depth pass / Selected texture / file).',
    ],
  },
  {
    id: 'mcp-server',
    name: 'AI Agents (MCP)',
    host: 'MCP server',
    platform: 'macOS · Windows',
    requirements: 'Node.js 18+ · any MCP client (Claude Desktop, Claude Code, …)',
    blurb:
      'Exposes your published workflows as tools for AI agents via the Model Context Protocol. ' +
      'Agents can list workflows, run them with files or prompts mid-conversation, and get the ' +
      'results back inline — outputs are also saved to disk.',
    href: '/integrations/mcp-server.zip',
    steps: [
      'Unzip somewhere permanent (e.g. ~/aicspro-mcp/) and run npm install in the folder (one time).',
      'Claude Code: claude mcp add aicspro --env AICS_TOKEN=<your Tool Key> -- node /path/to/mcp-server/index.js',
      'Claude Desktop: add the server to claude_desktop_config.json (command "node", args ' +
        '[".../index.js"], env AICS_TOKEN) — see the included README.',
      'Ask the agent to list workflows — each published workflow appears as a run_… tool.',
    ],
  },
];

function IntegrationCard({ integration }: { integration: Integration }) {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <CardTitle className="flex flex-wrap items-center gap-2">
              {integration.name}
              <Badge variant="secondary" className="shrink-0 font-normal">
                {integration.host}
              </Badge>
              <Badge variant="outline" className="shrink-0 font-normal">
                {integration.platform}
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">{integration.requirements}</CardDescription>
          </div>
          <Button
            asChild
            className={
              'shrink-0 bg-gradient-to-r from-violet-600 to-violet-700 ' +
              'hover:from-violet-700 hover:to-violet-800 ' +
              'shadow-lg shadow-violet-500/25 transition-all hover:shadow-xl hover:shadow-violet-500/30'
            }
          >
            <a href={integration.href} download>
              <Download className="h-4 w-4 mr-2" />
              Download
            </a>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">{integration.blurb}</p>
        <button
          onClick={() => setOpen((o) => !o)}
          className="mt-4 flex items-center gap-1.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
        >
          <ChevronDown className={'h-4 w-4 transition-transform ' + (open ? 'rotate-180' : '')} />
          Install steps
        </button>
        {open && (
          <ol className="mt-3 space-y-2 list-decimal list-inside text-sm text-muted-foreground">
            {integration.steps.map((step, i) => (
              <li key={i} className="pl-1">
                {step}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

export default function IntegrationsPage() {
  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8 overflow-auto">
      <div className="max-w-2xl mx-auto">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Puzzle className="h-6 w-6" />
            Integrations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Run AICS Pro workflows directly inside your creative tools. Download a panel and connect it with a Tool Key.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            All integrations run on <span className="font-medium text-foreground/80">macOS and Windows</span> — the same download
            works on both. macOS is our most-tested platform; if anything misbehaves on Windows, please report it.
          </p>
        </header>

        <div
          className={
            'p-4 rounded-lg border border-violet-300/50 dark:border-violet-500/30 ' +
            'bg-violet-50/50 dark:bg-violet-500/10 mb-6 flex items-center gap-3'
          }
        >
          <KeyRound className="h-5 w-5 text-violet-600 dark:text-violet-400 shrink-0" />
          <p className="text-sm text-foreground/80">
            Every integration connects with a <span className="font-medium">Tool Key</span>. Generate one on the{' '}
            <Link to="/tokens" className="font-medium text-violet-700 dark:text-violet-300 underline underline-offset-2">
              Tool Key
            </Link>{' '}
            page, then paste it into the panel.
          </p>
        </div>

        <div className="space-y-4">
          {INTEGRATIONS.map((integration) => (
            <IntegrationCard key={integration.id} integration={integration} />
          ))}
        </div>
      </div>
    </div>
  );
}
